import { type ErrorInfo, type FC, type ReactNode, useCallback } from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";

import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { trackEvent } from "../utils/analytics";
import axiosInstance from "../utils/networkWrapper";

function useSendErrorLogs() {
	const {
		chosenDepartment: { department_id },
	} = useChosenDepartment();

	return useCallback(
		async (error: unknown, errorInfo: ErrorInfo) => {
			const message = error instanceof Error ? error.message : String(error);
			const name = error instanceof Error ? error.name : "Unknown";

			trackEvent("Frontend error", {
				message,
				name,
				errorInfo,
			});

			await axiosInstance.post("/fe-logs", { message, name, errorInfo }, { params: { department_id } }).catch(console.error);
		},
		[department_id],
	);
}

export const ErrorBoundary: FC<{
	fallback?: ReactNode;
	/** Renders the children again once any of these values changes, e.g. the route a page boundary covers. */
	resetKeys?: unknown[];
	children: ReactNode;
}> = ({ fallback = null, resetKeys, children }) => {
	const sendErrorLog = useSendErrorLogs();

	return (
		<ReactErrorBoundary fallback={fallback} resetKeys={resetKeys} onError={sendErrorLog}>
			{children}
		</ReactErrorBoundary>
	);
};
